/**
 * PlayerModal Component
 * Shows detailed player information in a modal
 */

import { motion, AnimatePresence } from 'framer-motion';
import type { MissingPlayer, Position } from '@/types';

interface PlayerModalProps {
  player: MissingPlayer | null;
  isOpen: boolean;
  onClose: () => void;
}

const positionColors: Record<Position, string> = {
  TOP: '#ff6b6b',
  JUNGLE: '#4ecdc4',
  MID: '#ffe66d',
  ADC: '#ff6b9d',
  SUPPORT: '#95e1d3',
};

const positionNames: Record<Position, string> = {
  TOP: '上单',
  JUNGLE: '打野',
  MID: '中单',
  ADC: 'ADC',
  SUPPORT: '辅助',
};

export function PlayerModal({ player, isOpen, onClose }: PlayerModalProps) {
  if (!player) return null;

  const position = player.position as Position;
  const positionColor = positionColors[position] || '#a0a0b0';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-lg bg-surface border border-border rounded-2xl overflow-hidden z-50 max-h-[90vh] overflow-y-auto"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-background/50 text-secondary hover:text-primary transition-colors z-10"
            >
              ✕
            </button>

            {/* Player Photo */}
            <div className="relative h-64 bg-background">
              {player.photo_url ? (
                <img
                  src={player.photo_url}
                  alt={player.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-8xl font-bold text-tertiary/30">
                    {player.name_en.charAt(0)}
                  </span>
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-surface to-transparent" />
            </div>

            {/* Player Details */}
            <div className="p-6 -mt-8 relative">
              {/* Position Badge */}
              {position && (
                <div
                  className="inline-block px-3 py-1 rounded-full text-xs font-bold text-background mb-4"
                  style={{ backgroundColor: positionColor }}
                >
                  {positionNames[position]}
                </div>
              )}

              <h2 className="text-2xl font-bold text-primary mb-1">
                {player.name_en}
              </h2>
              <p className="text-secondary mb-6">{player.name}</p>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {player.country && (
                  <div>
                    <div className="text-tertiary text-xs mb-1">国籍</div>
                    <div className="text-secondary">{player.country}</div>
                  </div>
                )}
                {player.birthdate && (
                  <div>
                    <div className="text-tertiary text-xs mb-1">生日</div>
                    <div className="text-secondary">{player.birthdate}</div>
                  </div>
                )}
              </div>

              {/* Career Timeline */}
              <div className="border-t border-border pt-4">
                <h3 className="text-sm font-medium text-tertiary mb-3">职业生涯</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-status-active" />
                    <div className="flex-1">
                      <div className="text-secondary">2025 赛季</div>
                      <div className="text-primary font-medium">
                        {player.team_2025 || '自由人'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-status-retired" />
                    <div className="flex-1">
                      <div className="text-secondary">2026 赛季</div>
                      <div className="text-status-retired font-medium">
                        离场
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
